<script setup>

import {computed, onMounted, onUnmounted, ref, watch} from "vue";

const props = defineProps({
    logoUrl: {
        type: String,
        required: false,
        default: null
    }
})

const savingState = ref('')
const navigatables = ref(null)
const navigationItems = ref([])
const creatingNavigationItem = ref(false)
const editingNavigationItem = ref(null)
const movingNavigationItem = ref(null)
const iconSelectorOpen = ref(false)
const iconUploadOpen = ref(false)
const defaultFrontPageId = ref(null)
const displayMoreNavigation = ref(false)
const hasUnsavedChanges = ref(false)
const initialState = ref(null)
const targetLabels = {
    app_page: 'Page',
}

const colors = ref({
    primaryColor: '#000000',
    primaryContrastColor: '#ffffff',
    primaryBackgroundColor: '#fefefe',
    primaryForegroundColor: '#171717',
    secondaryBackgroundColor: '#fafafa',
    secondaryForegroundColor: '#050505',
    cardBackgroundColor: '#fafafa',
    cardForegroundColor: '#050505',
    cardBorderColor: '#d1d1d1',
    pinstripeColor: '#d1d1d1',
})

const loadSettings = async () => {
    try {
        const res = await axios.get(route('api.manage-whitelabel-app.settings'));
        if (Object.values(res.data.navigation).length > 0) {
            navigationItems.value = res.data.navigation;
        }
        if (res.data.colors.primaryColor) {
            colors.value.primaryColor = res.data.colors.primaryColor;
        }
        if (res.data.colors.primaryContrastColor) {
            colors.value.primaryContrastColor = res.data.colors.primaryContrastColor;
        }
        if (res.data.colors.primaryForegroundColor) {
            colors.value.primaryForegroundColor = res.data.colors.primaryForegroundColor;
        }
        if (res.data.colors.primaryBackgroundColor) {
            colors.value.primaryBackgroundColor = res.data.colors.primaryBackgroundColor;
        }
        if (res.data.colors.secondaryForegroundColor) {
            colors.value.secondaryForegroundColor = res.data.colors.secondaryForegroundColor;
        }
        if (res.data.colors.secondaryBackgroundColor) {
            colors.value.secondaryBackgroundColor = res.data.colors.secondaryBackgroundColor;
        }
        if (res.data.colors.cardForegroundColor) {
            colors.value.cardForegroundColor = res.data.colors.cardForegroundColor;
        }
        if (res.data.colors.cardBackgroundColor) {
            colors.value.cardBackgroundColor = res.data.colors.cardBackgroundColor;
        }
        if (res.data.colors.cardBorderColor) {
            colors.value.cardBorderColor = res.data.colors.cardBorderColor;
        }
        if (res.data.colors.pinstripeColor) {
            colors.value.pinstripeColor = res.data.colors.pinstripeColor;
        }
        if (res.data.default_front_page_id) {
            defaultFrontPageId.value = res.data.default_front_page_id;
        }

        initialState.value = {
            navigation: JSON.parse(JSON.stringify(navigationItems.value)),
            colors: JSON.parse(JSON.stringify(colors.value)),
            default_front_page_id: defaultFrontPageId.value
        }
    } catch (error) {
        console.error(error);
    }
}

const loadNavigatables = async () => {
    try {
        const res = await axios.get(route('api.manage-whitelabel-app.navigation-targets'));
        navigatables.value = res.data;
    } catch (error) {
        console.error(error);
    }
}

const updateNavigation = async () => {
    savingState.value = 'waiting'
    try {
        const res = await axios.post(route('api.manage-whitelabel-app.settings.store'), {
            navigation: navigationItems.value,
            colors: colors.value,
            default_front_page_id: defaultFrontPageId.value,
        });

        if (res.data.success) {
            savingState.value = 'success'
            hasUnsavedChanges.value = false
            initialState.value = {
                navigation: JSON.parse(JSON.stringify(navigationItems.value)),
                colors: JSON.parse(JSON.stringify(colors.value)),
                default_front_page_id: defaultFrontPageId.value
            }
        }
    } catch (error) {
        console.error(error);
        savingState.value = 'error'
    }
}

const editNavigationItem = (item) => {
    loadNavigatables();
    editingNavigationItem.value = item
}

const addNavigationItem = () => {
    creatingNavigationItem.value = true

    let newNavigationitem = {
        label: '',
        targetType: null,
        targetId: null,
        icon: 'fa-light fa-font-awesome',
    }

    editNavigationItem(newNavigationitem)
}

const startMovingNavigationItem = (item) => {
    movingNavigationItem.value = item
}

const moveNavigationItem = (item) => {
    if (movingNavigationItem.value === null) {
        return
    }

    const movingIndex = navigationItems.value.indexOf(movingNavigationItem.value)
    const targetIndex = navigationItems.value.indexOf(item)

    const movingItem = JSON.parse(JSON.stringify(movingNavigationItem.value))
    const targetItem = JSON.parse(JSON.stringify(item))

    navigationItems.value[movingIndex] = targetItem
    navigationItems.value[targetIndex] = movingItem

    movingNavigationItem.value = null
}

const deleteNavigationItem = (item) => {
    const index = navigationItems.value.indexOf(item)
    navigationItems.value.splice(index, 1)
    editingNavigationItem.value = null
}

const maybeFinishEditingNavigationItem = () => {
    if (navigationItemValidates.value) {
        if (creatingNavigationItem.value) {
            navigationItems.value.push(editingNavigationItem.value)
            creatingNavigationItem.value = false
        }

        editingNavigationItem.value = null
    }
}

const cancelEditingNavigationItem = () => {
    creatingNavigationItem.value = null
    editingNavigationItem.value = null
}

const setItemTarget = (item, target) => {
    item.targetType = target.type
    item.targetId = target.id
    if (item.label === '') {
        item.label = target.title
    }
}

const toggleMoreNavigation = () => {
    displayMoreNavigation.value = ! displayMoreNavigation.value
}

const handleCustomIconUploaded = ((file, data) => {
    editingNavigationItem.value.iconId = data.data.id
    editingNavigationItem.value.iconUrl = data.data.url
    iconUploadOpen.value = false
})

const clearCustomIcon = () => {
    editingNavigationItem.value.icon = null
    editingNavigationItem.value.iconUrl = null
    editingNavigationItem.value.iconId = null
}

const chromeStyle = computed(() => {
    return {
        backgroundColor: colors.value.secondaryBackgroundColor,
        color: colors.value.secondaryForegroundColor,
        borderColor: colors.value.pinstripeColor
    }
})

const backdropStyle = computed(() => {
    return {
        backgroundColor: colors.value.primaryBackgroundColor,
        color: colors.value.primaryForegroundColor
    }
})

const primaryNavigationItems = computed(() => {
    return navigationItems.value?.slice(0,primaryNavigationSlotCountAvailable.value) || []
})

const secondaryNavigationItems = computed(() => {
    return navigationItems.value?.slice(primaryNavigationSlotCountAvailable.value) || []
})

const navigationItemValidates = computed(() => {
    return editingNavigationItem.value.label.length > 0 && editingNavigationItem.value.targetId !== null
})

const showMoreButton = computed(() => {
    if (! navigationItems.value) {
        return false
    }
    return navigationItems.value.length > 5
})

const primaryNavigationSlotCountAvailable = computed(() => {
    return showMoreButton.value ? 4 : 5
})

const checkForUnsavedChanges = () => {
    if (!initialState.value) return false

    const currentState = {
        navigation: navigationItems.value,
        colors: colors.value,
        default_front_page_id: defaultFrontPageId.value
    }

    return JSON.stringify(currentState) !== JSON.stringify(initialState.value)
}

const handleBeforeUnload = (event) => {
    if (hasUnsavedChanges.value) {
        event.preventDefault()
        event.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return 'You have unsaved changes. Are you sure you want to leave?'
    }
}

watch(editingNavigationItem, () => {
    if (editingNavigationItem.value) {
        iconSelectorOpen.value = false
    }
}, {deep: true})

watch([navigationItems, colors, defaultFrontPageId], () => {
    hasUnsavedChanges.value = checkForUnsavedChanges()
}, {deep: true})

onMounted(() => {
    loadSettings();
    loadNavigatables();
    window.addEventListener('beforeunload', handleBeforeUnload);
});

onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
});

</script>

<template>
    <div>
        <frosted-bar>
            <div class="flex items-center justify-between space-x-4">
                <h1>App design</h1>
                <button-success
                    :state="savingState"
                    @click="updateNavigation"
                >Save and publish</button-success>
            </div>
        </frosted-bar>
        <div class="grid gap-4 pt-16 lg:grid-cols-2">
            <div class="relative">
                <div class="z-10 md:sticky top-16">
                    <div class="flex flex-col items-center justify-center">
                        <div
                            class="relative w-full max-w-xs shadow-2xl aspect-portraitVideo rounded-2xl"
                            :style="backdropStyle"
                        >
                            <div
                                class="absolute top-0 left-0 right-0 flex items-center justify-between font-bold tracking-wide transform -translate-y-px border-b h-14 rounded-t-2xl"
                                :style="chromeStyle"
                            >
                                <div class="w-1/4"></div>
                                <div class="w-1/2 text-center">Title</div>
                                <div class="flex items-center justify-end w-1/4 pr-6 space-x-2">
                                    <div class="rounded-md flex items-center space-x-2 py-px pl-1.5 pr-2" :style="{backgroundColor: colors.primaryColor}">
                                        <i class="text-sm fa-regular fa-chevron-down"
                                           :style="{color: colors.primaryContrastColor}"
                                        ></i>
                                        <i class="text-sm fa-regular fa-play"
                                           :style="{color: colors.primaryContrastColor}"
                                        ></i>
                                    </div>
                                    <i
                                        class="text-xl fa-regular fa-user-circle"
                                        :style="{color: colors.primaryColor}"
                                    ></i>
                                </div>

                            </div>

                            <div class="absolute left-0 right-0 px-6 py-3 overflow-y-scroll text-xs top-14 bottom-16">
<!--                                <img-->
<!--                                    v-if="logoUrl"-->
<!--                                    :src="logoUrl"-->
<!--                                    class="w-2/3 mx-auto mb-4"-->
<!--                                    alt="Logo"-->
<!--                                />-->

                                <h1 class="text-lg">This is your app</h1>
                                <p>It can contain many types of content, and here you can adjust the general look of that content.</p>
                                <p>We're automatically using your brand color, but you can adjust background, text and border colors to your liking.</p>

                                <div
                                    class="w-full px-6 py-4 mx-auto my-4 text-center border rounded-2xl"
                                    :style="{
                                        backgroundColor: colors.cardBackgroundColor,
                                        color: colors.cardForegroundColor,
                                        borderColor: colors.cardBorderColor,
                                    }"
                                >
                                    <h3 class="text-base">This is a card</h3>
                                    <p>
                                        It is used for widgets, for example.<br>
                                        They have input fields.
                                    </p>
                                    <label class="block mb-1">Input label</label>
                                    <input type="text" class="w-full px-2 py-1 mb-0 text-xs text-center border rounded-sm" placeholder="Type something..." maxlength="255" />
                                    <hr
                                        class="my-4 opacity-50"
                                        :style="{
                                        borderColor: colors.cardBorderColor,
                                    }"
                                    >
                                    <div class="flex justify-center">
                                        <div class="" :style="{color: colors.cardForegroundColor}">
                                            And buttons
                                        </div>
                                    </div>
                                </div>

                                <div
                                    class="w-full px-6 py-4 mx-auto my-4 text-center border rounded-2xl"
                                    :style="{
                                        backgroundColor: colors.cardBackgroundColor,
                                        color: colors.cardForegroundColor,
                                        borderColor: colors.cardBorderColor,
                                    }"
                                >
                                    <div class="flex items-center justify-start space-x-3">
                                        <i class="text-2xl fa-regular fa-circle-play " :style="{color: colors.cardForegroundColor}"></i>
                                        <span>Audio track name shown here</span>
                                    </div>
                                    <div class="flex items-center space-x-4">
                                        <div class="w-full">
                                            <div
                                                class="relative h-1 rounded-md"
                                                :style="{
                                                    color: colors.cardBackgroundColor,
                                                    backgroundColor: colors.cardForegroundColor,
                                                    borderColor: colors.cardBorderColor,
                                                }"
                                            >
                                                <div class="absolute rounded-full w-3 h-3 top-0.5 left-8 transform translate-x-1/2 -translate-y-1/2" :style="{backgroundColor: colors.primaryColor}"></div>
                                                <div class="absolute top-0 bottom-0 left-0 w-10 rounded-full" :style="{backgroundColor: colors.primaryColor}"></div>
                                            </div>
                                        </div>
                                        <div class="flex items-center space-x-3">
                                            <i class="text-lg fa-regular fa-backward " :style="{color: colors.cardForegroundColor}"></i>
                                            <span>1:42</span>
                                            <i class="text-lg fa-regular fa-forward" :style="{color: colors.cardForegroundColor}"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="absolute bottom-0 left-0 right-0">
                                <div
                                    class="relative flex items-stretch justify-around h-16 translate-y-px border-t rounded-b-2xl tranform"
                                    :style="chromeStyle"
                                >
                                    <div
                                        v-for="(item, i) in primaryNavigationItems"
                                        :key="i"
                                        class="relative flex flex-col items-center justify-center w-1/5 cursor-pointer group"
                                        :style="i === 0 ? {color: colors.primaryColor} : {}"
                                    >
                                        <div
                                            :class="movingNavigationItem ? 'opacity-20' : ''"
                                            class="w-6 h-6 flex items-center justify-center"
                                        >
                                            <img v-if="item.iconUrl" :src="item.iconUrl"  alt="Custom icon" />
                                            <i
                                                v-else
                                                :class="['fa-'+item.icon]"
                                                class="text-lg fa-light"
                                            ></i>
                                        </div>
                                        <div
                                            :class="movingNavigationItem ? 'opacity-20' : ''"
                                            class="w-full text-center truncate text-xxs"
                                        >{{ item.label }}</div>

                                        <div
                                            v-if="movingNavigationItem && i !== 0"
                                            class="absolute inset-0 flex items-center justify-center hover:bg-gray-100/50"
                                            @click="moveNavigationItem(item)"
                                        >
                                            <i class="text-xl transform -translate-y-1 fa-solid fa-arrow-down-to-dotted-line"></i>
                                        </div>

                                        <div
                                            v-if="! movingNavigationItem"
                                            class="absolute left-0 right-0 z-10 items-center hidden space-x-1 group-hover:flex -top-2"
                                        >
                                            <button
                                                v-if="i !== 0"
                                                class="bg-gray-100 text-xs rounded-md px-1 py-0.5 space-x-2 text-gray-500"
                                                @click="startMovingNavigationItem(item)"
                                            >Move</button>
                                            <button
                                                class="bg-gray-100 text-xs rounded-md px-1 py-0.5 space-x-2 text-gray-500"
                                                @click="editNavigationItem(item)"
                                            >Edit</button>
                                        </div>
                                    </div>

                                    <div
                                        v-if="primaryNavigationSlotCountAvailable === 4"
                                        class="relative flex flex-col items-center justify-center w-1/5 cursor-pointer group"
                                        @click="toggleMoreNavigation"
                                    >
                                        <i
                                            class="text-lg opacity-50 fa-light fa-ellipsis"
                                        ></i>
                                        <div
                                            class="w-full text-center truncate opacity-50 text-xxs"
                                        >More</div>
                                    </div>

                                    <div
                                        v-if="primaryNavigationSlotCountAvailable === 4 && displayMoreNavigation"
                                        class="absolute mb-2 border rounded-lg right-2 bottom-full"
                                        :style="chromeStyle"
                                    >
                                        <ul class="py-1">
                                            <li
                                                v-for="(item, i) in secondaryNavigationItems"
                                                :key="i"
                                                class="relative flex items-center px-4 py-1 space-x-2 cursor-pointer max-w-64 group"
                                            >
                                                <div
                                                    :class="movingNavigationItem ? 'opacity-20' : ''"
                                                    class="w-6 h-6 flex items-center justify-center"
                                                >
                                                    <img v-if="item.iconUrl" :src="item.iconUrl"  alt="Custom icon" />
                                                    <i
                                                        v-else
                                                        :class="['fa-'+item.icon]"
                                                        class="text-lg fa-light"
                                                    ></i>
                                                </div>
                                                <div
                                                    class="text-xs truncate"
                                                    :class="movingNavigationItem ? 'opacity-20' : ''"
                                                >
                                                    {{ item.label }}
                                                </div>

                                                <div
                                                    v-if="movingNavigationItem"
                                                    class="absolute inset-0 flex items-center justify-center hover:bg-gray-100/50"
                                                    @click="moveNavigationItem(item)"
                                                >
                                                    <i class="text-xl transform -translate-y-1 fa-solid fa-arrow-down-to-dotted-line"></i>
                                                </div>

                                                <div
                                                    v-if="! movingNavigationItem"
                                                    class="absolute left-0 right-0 items-end hidden w-full space-x-4 group-hover:flex -top-1"
                                                >
                                                    <button
                                                        class="bg-gray-100 text-xs rounded-md px-1 py-0.5 space-x-2 text-gray-500"
                                                        @click="startMovingNavigationItem(item)"
                                                    >Move</button>
                                                    <button
                                                        class="bg-gray-100 text-xs rounded-md px-1 py-0.5 space-x-2 text-gray-500"
                                                        @click="editNavigationItem(item)"
                                                    >Edit</button>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="flex justify-center mt-4">
                        <button-secondary
                            v-if="navigationItems?.length < 12"
                            size="sm"
                            @click="addNavigationItem()"
                        >
                            Add navigation item
                        </button-secondary>
                    </div>
                </div>
            </div>

            <div class="relative z-10 mb-8">
                <div class="mb-12">
                    <h2>Colors</h2>
                    <p class="text-xs italic text-gray-500">
                        These colors should not be changed frequently, since they require updating the screenshots in App Store and Play Store.
                    </p>
                    <whitelabel-app-design-editor
                        :primary-color="colors.primaryColor"
                        :primary-contrast-color="colors.primaryContrastColor"
                        :primary-background-color="colors.primaryBackgroundColor"
                        :primary-foreground-color="colors.primaryForegroundColor"
                        :secondary-background-color="colors.secondaryBackgroundColor"
                        :secondary-foreground-color="colors.secondaryForegroundColor"
                        :card-background-color="colors.cardBackgroundColor"
                        :card-foreground-color="colors.cardForegroundColor"
                        :card-border-color="colors.cardBorderColor"
                        :pinstripe-color="colors.pinstripeColor"
                        @update:colors="colors = $event"
                    ></whitelabel-app-design-editor>
                </div>
            </div>

            <slideout-with-slots
                v-if="editingNavigationItem !== null"
                @close="editingNavigationItem = null"
                :allow-self-close="false"
                @self-close-prevented="maybeFinishEditingNavigationItem"
            >
                <template #body>
                    <input-label label="Select a target" />
                    <ul class="px-4 py-2 mt-1 mb-4 -mx-4 bg-gray-100 md:-mx-6 md:px-6 border-y">
                        <li
                            v-for="(target, i) in navigatables?.pages"
                            @click="setItemTarget(editingNavigationItem, target)"
                            class="py-0.5 cursor-pointer -mx-4 md:-mx-6 px-4 md:px-6"
                            :class="{'bg-gray-300': editingNavigationItem.targetId === target.id}"
                        >
                            {{ target.title }}
                            <pill class="bg-gray-200" size="xs">
                                {{ targetLabels[target.type] }}
                            </pill>
                        </li>
                    </ul>

                    <input-label label="Icon and label" />
                    <div class="relative flex items-center space-x-2">
                        <div class="flex items-center px-2 mb-3">
                            <span
                                v-if="editingNavigationItem?.iconUrl"
                            >
                                <img
                                    :src="editingNavigationItem.iconUrl"
                                    alt="Custom icon"
                                    class="w-8 h-8 object-contain"
                                />
                            </span>
                            <span v-else>
                                <i
                                    v-if="editingNavigationItem?.icon"
                                   :class="'fa-'+editingNavigationItem?.icon"
                                    class="text-lg fa-light"
                                ></i>
                                <i v-else class="text-lg text-gray-400 fa-light fa-question"></i>
                            </span>
                        </div>
                        <input-text
                            class="w-full"
                            v-model:content="editingNavigationItem.label"
                        ></input-text>

                        <icon-selector-for-app-modal
                            v-if="iconSelectorOpen"
                            class="absolute inset-0 z-20"
                            @close="iconSelectorOpen = false"
                            @select-icon="editingNavigationItem.icon = $event.faName"
                        ></icon-selector-for-app-modal>

                        <div
                            v-if="iconUploadOpen"
                            class="absolute top-0 left-0 right-0 z-modal bg-white border shadow-lg p-4 rounded-wl"
                        >
                            <page-whitelabel-app-custom-icon-selector
                                @uploaded="handleCustomIconUploaded"
                            ></page-whitelabel-app-custom-icon-selector>
                            <button-secondary
                                @click="iconUploadOpen = false"
                                size="sm"
                                class="mt-4"
                            >Cancel</button-secondary>
                        </div>
                    </div>

                    <div class="mb-8 flex space-x-4">
                        <button-base
                            v-if="! editingNavigationItem.iconId"
                            size="sm"
                            @click="iconSelectorOpen = true"
                        >
                            Change icon
                        </button-base>
                        <button-base
                            v-if="! editingNavigationItem.iconId"
                            size="sm"
                            @click="iconUploadOpen = true"
                        >
                            Upload custom icon
                        </button-base>
                        <button-danger
                            v-if="editingNavigationItem.iconId"
                            size="sm"
                            @click="clearCustomIcon"
                        >Remove custom icon</button-danger>

                    </div>


                    <div class="flex justify-between space-x-4">
                        <div>
                            <button-with-confirmation
                                v-if="primaryNavigationItems[0] !== editingNavigationItem"
                                @confirmed="deleteNavigationItem(editingNavigationItem)"
                                confirmButtonLabel="Delete"
                                confirm-text="Are you sure you want to delete this item?"
                                modalClass="absolute -top-2 -left-2 p-2 bg-white text-black rounded shadow-md z-20"

                            >
                                Delete
                            </button-with-confirmation>
                        </div>
                        <div class="flex justify-end space-x-4">
                            <button-base
                                @click="cancelEditingNavigationItem"
                            >
                                Cancel
                            </button-base>
                            <button-base
                                :style-type="navigationItemValidates ? 'success' : 'disabled'"
                                :disabled="! navigationItemValidates"
                                @click="maybeFinishEditingNavigationItem"
                            >
                                {{ creatingNavigationItem ? 'Add' : 'Save' }}
                            </button-base>
                        </div>
                    </div>
                </template>
            </slideout-with-slots>
        </div>
    </div>
</template>

<style scoped>

</style>
